import { motion } from "framer-motion";
import { MapPin, Target, Heart, Compass } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { TEAM, TEAM_PHOTO, FOUNDER, CONTACT } from "../data/site";

const PHILOSOPHY = [
  { icon: Compass, title: "Curiosity First", text: "We start with wonder. Questions before answers, always." },
  { icon: Target, title: "Mastery, Not Memory", text: "Deep understanding that outlasts any exam." },
  { icon: Heart, title: "Every Child Seen", text: "Small batches so no learner ever slips through the cracks." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32">
      {/* Founder story */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <Reveal>
          <span className="font-bold text-teal-brand">OUR STORY</span>
          <h1 className="font-display font-extrabold text-5xl sm:text-7xl text-navy mt-2 leading-[0.95] max-w-4xl">
            We started because <span className="text-marigold">classrooms</span> were losing curiosity.
          </h1>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-12 mt-12">
          <Reveal delay={0.1}>
            <p className="text-lg text-navy/75 leading-relaxed">
              The Thinkstitute began in a small room in Vesu with one stubborn
              belief held by founder <span className="font-bold text-navy">Chirag Kankaria</span>:
              that children don&apos;t dislike learning — they dislike being talked at.
              After years of teaching, he set out to build a place where Grades
              7–12 students rediscover the joy of figuring things out.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-lg text-navy/75 leading-relaxed">
              Today, a team of subject specialists coaches CBSE, Commerce and IB
              MYP students across Surat — not with louder lectures or thicker
              notes, but with better questions, smaller batches, and mentors who
              genuinely care. Every syllabus is covered, but the real win is a
              child who walks in curious and walks out confident.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Founder spotlight */}
      <section className="bg-white border-y border-navy/10" data-testid="founder-spotlight">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-2">
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-marigold/20 -rotate-3" />
              <div className="absolute -inset-3 rounded-[2rem] bg-teal-brand/15 rotate-2" />
              <img
                src={FOUNDER.photo}
                alt={FOUNDER.name}
                className="relative rounded-[2rem] w-full aspect-[4/5] object-cover object-top border border-navy/10"
              />
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <span className="font-bold text-teal-brand">FROM THE FOUNDER</span>
            <h2 className="font-display font-extrabold text-3xl sm:text-5xl text-navy mt-2 leading-tight">
              Why The Thinkstitute exists.
            </h2>
            <div className="mt-6 space-y-4">
              {FOUNDER.story.map((p, i) => (
                <p key={i} className="text-navy/75 text-base sm:text-lg leading-relaxed">{p}</p>
              ))}
            </div>
            <div className="mt-7 flex items-center gap-4">
              <span className="h-px w-10 bg-marigold" />
              <div>
                <p className="font-display font-extrabold text-navy text-xl">{FOUNDER.name}</p>
                <p className="text-sm font-semibold text-marigold">{FOUNDER.role}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      {/* Team photo banner */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20" data-testid="team-photo">
        <Reveal>
          <div className="relative rounded-[2rem] overflow-hidden border border-navy/10">
            <img
              src={TEAM_PHOTO}
              alt="The Thinkstitute team"
              className="w-full h-[360px] sm:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12">
              <p className="font-display font-extrabold text-cream text-3xl sm:text-5xl leading-tight max-w-2xl">
                One team, wholly invested in your child.
              </p>
              <p className="mt-3 text-cream/80 max-w-xl">
                The people behind The Thinkstitute — Vesu, Surat.
              </p>
            </div>
          </div>
        </Reveal>
      </section>


      {/* Philosophy */}
      <section className="bg-indigo-brand text-cream py-24" data-testid="philosophy">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="mb-14 max-w-2xl">
            <span className="font-bold text-marigold">OUR PHILOSOPHY</span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl mt-2">
              What we believe in.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {PHILOSOPHY.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="rounded-3xl bg-cream/10 border border-cream/15 p-8 h-full">
                  <p.icon className="text-teal-brand" size={36} />
                  <h3 className="font-display font-bold text-2xl mt-5">{p.title}</h3>
                  <p className="mt-2 text-cream/75">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24" data-testid="team">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="mb-14 max-w-2xl">
            <span className="font-bold text-teal-brand">THE SPECIALISTS</span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-navy mt-2">
              Seven mentors. CBSE &amp; IB.
            </h2>
            <p className="mt-3 text-navy/70 text-lg">A close-knit team of subject specialists who make learning click.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <motion.div whileHover={{ y: -6 }} className="rounded-3xl bg-white border border-navy/10 overflow-hidden h-full" data-testid={`team-member-${i}`}>
                  <div className="relative aspect-[4/5]">
                    {m.photo ? (
                      <img src={m.photo} alt={m.name} loading="lazy" className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full grid place-items-center font-display font-extrabold text-white text-6xl" style={{ backgroundColor: m.color }}>
                        {m.name.replace(/[^A-Za-z ]/g, "").split(" ").filter(Boolean).map((n) => n[0]).slice(0, 2).join("")}
                      </div>
                    )}
                    <span className="absolute top-3 right-3 rounded-full bg-white/90 text-navy/70 text-[11px] font-bold px-2.5 py-1">{m.wing}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-sans font-bold text-lg text-navy tracking-tight">{m.name}</h3>
                    <p className="text-sm font-semibold text-marigold">{m.role}</p>
                    <p className="mt-3 text-navy/70 text-sm">{m.bio}</p>
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="pb-24" data-testid="location">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="grid lg:grid-cols-2 gap-8 items-stretch rounded-[2rem] overflow-hidden border border-navy/10 bg-white">
              <div className="p-10 sm:p-14 flex flex-col justify-center">
                <MapPin className="text-marigold" size={40} />
                <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-navy mt-5">
                  Come say hello in Vesu.
                </h2>
                <p className="mt-4 text-navy/75 text-lg">{CONTACT.address}</p>
                <a
                  href={`tel:${CONTACT.phone}`}
                  className="mt-6 font-bold text-teal-brand text-lg hover:text-marigold transition-colors"
                >
                  {CONTACT.phoneDisplay}
                </a>
              </div>
              <div className="min-h-[320px]">
                <iframe
                  title="The Thinkstitute location"
                  src={CONTACT.mapEmbed}
                  className="w-full h-full min-h-[320px] border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
